import { Field } from './types';

// --- type guards
const isStringArray = (x: unknown): x is string[] =>
  Array.isArray(x) && x.every(v => typeof v === 'string');

const isFieldArray = (x: unknown): x is Field[] =>
  Array.isArray(x) && x.every(v => v && typeof v === 'object' && 'value' in v);

// --- core loader (backward compatible)
export const loadReportsFromStorage = (
  storageKey: string,
  allReports?: Field[],
  defaultReports?: Field[]
): Field[] => {
  const all = allReports ?? [];
  const def = defaultReports ?? [];

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return def;

    const parsed: unknown = JSON.parse(raw);

    // --- New format: string[]
    if (isStringArray(parsed)) {
      if (all.length === 0) {
        // Can't resolve to Field[] without a catalog → fallback to defaults
        return def;
      }
      const byValue = new Map(all.map(r => [r.value, r]));
      const resolved = parsed
        .map(v => byValue.get(v))
        .filter((x): x is Field => Boolean(x));
      return resolved.length ? resolved : def;
    }

    // --- Old format: Field[]
    if (isFieldArray(parsed)) {
      if (all.length > 0) {
        const allowed = new Set(all.map(r => r.value));
        const normalized = parsed
          .filter(f => allowed.has(f.value))
          .map(f => all.find(r => r.value === f.value))
          .filter((r): r is Field => Boolean(r))
          .filter(
            (function () {
              const seen = new Set<string>();
              return (r: Field) => {
                if (seen.has(r.value)) return false;
                seen.add(r.value);
                return true;
              };
            })()
          );

        // Migrate to new format
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify(normalized.map(f => f.value))
          );
        } catch {
          /* ignore quota/privacy errors */
        }

        return normalized.length ? normalized : def;
      }

      // No catalog → return as-is (still valid), migrate to string[] for next time
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify(parsed.map(f => f.value))
        );
      } catch {
        /* ignore */
      }
      return parsed;
    }

    // Unknown/corrupt format → defaults
    return def;
  } catch {
    return def;
  }
};

// --- saver (always write new format: string[])
export const saveReportsToStorage = ({
  storageKey,
  reports,
}: {
  storageKey: string;
  reports: Field[];
}) => {
  try {
    const values = reports.map(r => r.value);
    localStorage.setItem(storageKey, JSON.stringify(values));
  } catch {
    /* ignore storage errors */
  }
};

export const initializeReportsFromStorage = ({
  allReports,
  storageKey,
  defaultReports,
}: {
  allReports?: Field[];
  storageKey: string;
  defaultReports?: Field[];
}) => {
  return loadReportsFromStorage(storageKey, allReports, defaultReports);
};
